export const DEFAULT_LIST_PAGE_SIZE = 20
export const MAX_LIST_PAGE_SIZE = 50

export function clampPageSize(pageSize: number): number {
  if (!Number.isFinite(pageSize)) return DEFAULT_LIST_PAGE_SIZE
  return Math.min(MAX_LIST_PAGE_SIZE, Math.max(1, Math.floor(pageSize)))
}

export function totalPages(total: number, pageSize: number): number {
  if (total <= 0) return 1
  return Math.max(1, Math.ceil(total / clampPageSize(pageSize)))
}

export function clampPage(page: number, total: number, pageSize: number): number {
  const maxPage = totalPages(total, pageSize)
  if (!Number.isFinite(page) || page < 1) return 1
  return Math.min(page, maxPage)
}

/**
 * Slice a display page over a list that starts with `stickyCount` pinned rows
 * followed by a separately paged suggestion list.
 */
export function stickyAwareListWindow(
  displayPage: number,
  pageSize: number,
  stickyCount: number,
): {
  stickyStart: number
  stickyLimit: number
  suggestionOffset: number
  suggestionLimit: number
} {
  const size = clampPageSize(pageSize)
  const page = !Number.isFinite(displayPage) || displayPage < 1 ? 1 : Math.floor(displayPage)
  const pinned = Math.max(0, Math.floor(stickyCount))
  const rangeStart = (page - 1) * size
  const rangeEnd = rangeStart + size

  const stickyStart = Math.min(pinned, rangeStart)
  const stickyLimit = Math.max(0, Math.min(pinned, rangeEnd) - stickyStart)
  const suggestionOffset = Math.max(0, rangeStart - pinned)
  const suggestionLimit = Math.max(0, Math.max(0, rangeEnd - pinned) - suggestionOffset)

  return { stickyStart, stickyLimit, suggestionOffset, suggestionLimit }
}

/** Fixed-size API pages needed to cover a suggestion offset window. */
export function apiPagesForOffsetWindow(
  offset: number,
  limit: number,
  pageSize: number,
): number[] {
  if (limit <= 0) return []
  const size = clampPageSize(pageSize)
  const start = Math.max(0, Math.floor(offset))
  const startPage = Math.floor(start / size) + 1
  const endPage = Math.floor((start + limit - 1) / size) + 1
  const pages: number[] = []
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page)
  }
  return pages
}

/** Compact page numbers with ellipsis for shadcn pagination. */
export function buildPageNumberWindow(
  currentPage: number,
  totalPageCount: number,
  siblingCount = 1,
): Array<number | 'ellipsis'> {
  if (totalPageCount <= 1) return [1]

  const pages = new Set<number>([1, totalPageCount, currentPage])
  for (let i = 1; i <= siblingCount; i += 1) {
    pages.add(currentPage - i)
    pages.add(currentPage + i)
  }

  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPageCount).sort((a, b) => a - b)
  const result: Array<number | 'ellipsis'> = []
  let prev = 0
  for (const page of sorted) {
    if (prev && page - prev > 1) result.push('ellipsis')
    result.push(page)
    prev = page
  }
  return result
}

export type PaginatedListMeta = {
  page: number
  page_size: number
  total: number
  has_next: boolean
}

export function appendListPageParams(
  params: URLSearchParams,
  page: number,
  pageSize: number,
): void {
  params.set('page', String(page))
  params.set('page_size', String(clampPageSize(pageSize)))
}

/** Read `page` from list URL search params (default 1). */
export function readListPage(searchParams: URLSearchParams): number {
  return parseInt(searchParams.get('page') || '1', 10) || 1
}

/** Write `page` into list URL search params (omit page 1). */
export function writeListPage(searchParams: URLSearchParams, page: number): URLSearchParams {
  const next = new URLSearchParams(searchParams)
  if (page <= 1) next.delete('page')
  else next.set('page', String(page))
  return next
}

/** Current list URL (path + query) to restore after opening a detail. */
export function listReturnPathFromLocation(location: {
  pathname: string
  search: string
}): string {
  return `${location.pathname}${location.search}`
}

export function isCandidateJobsListPathname(pathname: string): boolean {
  return pathname === '/candidate/jobs' || pathname === '/candidate/jobs/'
}

export function isEmployerJobsListPathname(pathname: string): boolean {
  if (pathname === '/employer/jobs' || pathname === '/employer/jobs/') return true
  return /^\/employer\/organisations\/[^/]+\/jobs\/?$/.test(pathname)
}

/** Safe `backPath` from location state — same list only, no off-site URLs. */
export function readListReturnPath(
  state: unknown,
  isAllowedPathname: (pathname: string) => boolean,
): string | undefined {
  const raw = (state as { backPath?: unknown } | null)?.backPath
  if (typeof raw !== 'string' || !raw.startsWith('/')) return undefined
  try {
    const url = new URL(raw, 'http://local.invalid')
    if (url.origin !== 'http://local.invalid') return undefined
    if (!isAllowedPathname(url.pathname)) return undefined
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return undefined
  }
}

export const PAGE_PANE_SCROLL_ATTR = 'data-page-pane-scroll'
const LIST_SCROLL_STORAGE_PREFIX = 'yh:list-scroll:'

export function isCandidateJobDetailPathname(pathname: string): boolean {
  if (isCandidateJobsListPathname(pathname)) return false
  return pathname.startsWith('/candidate/jobs/')
}

export function isEmployerJobDetailPathname(pathname: string): boolean {
  if (isEmployerJobsListPathname(pathname)) return false
  return pathname.startsWith('/employer/jobs/')
}

export function isJobsListPathname(pathname: string): boolean {
  return isCandidateJobsListPathname(pathname) || isEmployerJobsListPathname(pathname)
}

export function isJobsDetailPathname(pathname: string): boolean {
  return isCandidateJobDetailPathname(pathname) || isEmployerJobDetailPathname(pathname)
}

function readNonNegativeScroll(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined
  return value
}

export function readListScrollTop(state: unknown): number | undefined {
  return readNonNegativeScroll((state as { listScrollTop?: unknown } | null)?.listScrollTop)
}

export function readRestoreScrollTop(state: unknown): number | undefined {
  return readNonNegativeScroll((state as { restoreScrollTop?: unknown } | null)?.restoreScrollTop)
}

export function listReturnBackState(detailState: unknown): { restoreScrollTop: number } | undefined {
  const top = readListScrollTop(detailState)
  return top != null ? { restoreScrollTop: top } : undefined
}

export function readPagePaneScrollTop(): number {
  const el = document.querySelector(`[${PAGE_PANE_SCROLL_ATTR}]`)
  return el instanceof HTMLElement ? el.scrollTop : 0
}

function sessionStorageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function sessionStorageSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* private mode */
  }
}

function sessionStorageRemove(key: string): void {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* private mode */
  }
}

export function rememberListScroll(path: string, top: number): void {
  sessionStorageSet(`${LIST_SCROLL_STORAGE_PREFIX}${path}`, String(Math.round(top)))
}

export function takeRememberedListScroll(path: string): number | undefined {
  const key = `${LIST_SCROLL_STORAGE_PREFIX}${path}`
  const raw = sessionStorageGet(key)
  if (raw == null) return undefined
  sessionStorageRemove(key)
  const top = Number(raw)
  return Number.isFinite(top) && top >= 0 ? top : undefined
}

/** List URL + scroll so Back (and browser back) can return to the same spot. */
export function captureListReturnState(location: { pathname: string; search: string }): {
  backPath: string
  listScrollTop: number
} {
  const backPath = listReturnPathFromLocation(location)
  const listScrollTop = readPagePaneScrollTop()
  rememberListScroll(backPath, listScrollTop)
  return { backPath, listScrollTop }
}
